import { Arc, Group, Line, Rect } from 'react-konva';

import { OPENING_COLORS, isValidOpeningPreviewLayout } from '../lib/floorPlanOpening2D.js';
import {
  buildDoorSwing2DGeometry,
  resolveDoorSwingFields,
} from '../lib/floorPlanDoorSwing2D.js';
import { DEFAULT_DOOR, DEFAULT_WINDOW } from '../lib/floorPlanDesignerState.js';

/**
 * @param {[number, number] | undefined} point
 */
function isFinitePoint(point) {
  return (
    Array.isArray(point) &&
    point.length >= 2 &&
    Number.isFinite(point[0]) &&
    Number.isFinite(point[1])
  );
}

/**
 * @param {{
 *   type: 'door' | 'window';
 *   layout: {
 *     x: number;
 *     y: number;
 *     widthPx: number;
 *     thicknessPx: number;
 *     rotationDeg: number;
 *   };
 *   valid?: boolean;
 *   displayScale: number;
 *   maxPreviewWidthPx?: number;
 *   p0?: [number, number];
 *   p1?: [number, number];
 *   angle?: number;
 * }} props
 */
function FloorPlanOpeningPlacementPreview({
  type,
  layout,
  valid = true,
  displayScale,
  maxPreviewWidthPx = Infinity,
  p0,
  p1,
  angle = 0,
}) {
  if (
    !valid ||
    !isValidOpeningPreviewLayout(layout, maxPreviewWidthPx) ||
    !Number.isFinite(angle)
  ) {
    return null;
  }

  const color = type === 'door' ? OPENING_COLORS.door : OPENING_COLORS.window;
  const strokeWidth = Math.max(1.2 / displayScale, 0.75);

  if (type === 'door' && isFinitePoint(p0) && isFinitePoint(p1)) {
    const swing = resolveDoorSwingFields({});
    const geometry = buildDoorSwing2DGeometry({
      p0,
      p1,
      angle,
      hingeSide: swing.hingeSide,
      swingSide: swing.swingSide,
      doorState: 'open',
    });

    const arcLineWidth = Math.max(1 / displayScale, 0.6);
    const canShowArc =
      geometry.arc &&
      Number.isFinite(geometry.arc.x) &&
      Number.isFinite(geometry.arc.y) &&
      Number.isFinite(geometry.arc.radius) &&
      Number.isFinite(geometry.arc.rotationDeg) &&
      Number.isFinite(geometry.arc.angleDeg) &&
      geometry.arc.radius > 0 &&
      geometry.arc.radius < maxPreviewWidthPx;
    const arcInner = canShowArc ? Math.max(geometry.arc.radius - arcLineWidth, 0) : 0;
    const canShowLeaf =
      geometry.leafEnd &&
      isFinitePoint(geometry.hinge) &&
      isFinitePoint(geometry.leafEnd) &&
      Math.hypot(geometry.leafEnd[0] - geometry.hinge[0], geometry.leafEnd[1] - geometry.hinge[1]) <
        maxPreviewWidthPx;

    return (
      <Group listening={false}>
        <Rect
          x={layout.x}
          y={layout.y}
          width={layout.widthPx}
          height={layout.thicknessPx}
          offsetX={layout.widthPx / 2}
          offsetY={layout.thicknessPx / 2}
          rotation={layout.rotationDeg}
          fillEnabled={false}
          stroke={color}
          strokeWidth={strokeWidth}
          strokeScaleEnabled={false}
        />
        {canShowLeaf ? (
          <Line
            points={[geometry.hinge[0], geometry.hinge[1], geometry.leafEnd[0], geometry.leafEnd[1]]}
            stroke={color}
            strokeWidth={strokeWidth}
            strokeScaleEnabled={false}
            lineCap="round"
          />
        ) : null}
        {canShowArc ? (
          <Arc
            x={geometry.arc.x}
            y={geometry.arc.y}
            innerRadius={arcInner}
            outerRadius={geometry.arc.radius}
            angle={geometry.arc.angleDeg}
            rotation={geometry.arc.rotationDeg}
            stroke={color}
            strokeWidth={arcLineWidth}
            strokeScaleEnabled={false}
            fillEnabled={false}
          />
        ) : null}
      </Group>
    );
  }

  if (type === 'window') {
    const halfLine = layout.widthPx * 0.35;
    const cos = Math.cos((layout.rotationDeg * Math.PI) / 180);
    const sin = Math.sin((layout.rotationDeg * Math.PI) / 180);
    const lineStartX = layout.x - halfLine * cos;
    const lineStartY = layout.y - halfLine * sin;
    const lineEndX = layout.x + halfLine * cos;
    const lineEndY = layout.y + halfLine * sin;

    if (
      !Number.isFinite(lineStartX) ||
      !Number.isFinite(lineStartY) ||
      !Number.isFinite(lineEndX) ||
      !Number.isFinite(lineEndY)
    ) {
      return null;
    }

    return (
      <Group listening={false}>
        <Rect
          x={layout.x}
          y={layout.y}
          width={layout.widthPx}
          height={layout.thicknessPx}
          offsetX={layout.widthPx / 2}
          offsetY={layout.thicknessPx / 2}
          rotation={layout.rotationDeg}
          fillEnabled={false}
          stroke={color}
          strokeWidth={strokeWidth}
          strokeScaleEnabled={false}
        />
        <Line
          points={[lineStartX, lineStartY, lineEndX, lineEndY]}
          stroke={color}
          strokeWidth={strokeWidth}
          strokeScaleEnabled={false}
          lineCap="round"
        />
      </Group>
    );
  }

  return null;
}

export { DEFAULT_DOOR, DEFAULT_WINDOW };
export default FloorPlanOpeningPlacementPreview;
